<template>
    <div>
        <div class="d-flex align-items-center mb-3" id="container-options" :style="hideForLocationUser">
            <div v-if="!isLocationUser">
                <input type="checkbox" style="margin: 0 22px;" id="invites-all"
                       :checked="allInvitesSelected"
                       @click="handleAllInvites"
                       v-if="invites.data.length"
                />
                <button class="btn btn-primary"
                        :disabled="!enableSendInvite"
                        @click="openBulkInvitesModal">
                    Send Invite
                </button>

                <span class="d-inline-block" tabindex="0" data-toggle="tooltip"
                      title="Only scheduled invites can be deleted" id="delete-scheduled-invite">
                    <button type="button" class="btn btn-danger"
                            :style="{ 'pointer-events': !enableDeleteInvite ? 'none' : null }"
                            :disabled="!enableDeleteInvite"
                            @click="openDeleteScheduledInvitesModal">
                        Delete
                    </button>
                </span>
                <label class="ml-2" v-if="selectedInvites.length">
                    {{ selectedInvites.length }} selected
                </label>
            </div>
            <div class="d-flex flex-row" id="options">
                <select class="form-control ml-2 select-filter" v-model="filterBy" @change="fetchInvites">
                    <option value="" selected="selected">All</option>
                    <option value="sent">Sent</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="opened">Opened</option>
                    <option value="shared">Shared</option>
                    <option value="failed">Failed</option>
                </select>
                <select class="form-control ml-2 select-filter" @change="fetchInvites" v-model="sortBy">
                    <option value="sent_at|desc">Sent at (Newest first)</option>
                    <option value="sent_at|asc">Sent at (Oldest first)</option>
                    <option value="send_at|desc">Send at (Newest first)</option>
                    <option value="send_at|asc">Send at (Oldest first)</option>
                    <option value="campaigns.type|asc">Type (A-Z)</option>
                    <option value="campaigns.type|desc">Type (Z-A)</option>
                    <option value="origin|asc">Origin (A-Z)</option>
                    <option value="origin|desc">Origin (Z-A)</option>
                </select>
                <select class="form-control ml-2 select-filter" @change="fetchInvites" v-model="paginationQuantity">
                    <option value="25">25 rows</option>
                    <option value="50">50 rows</option>
                    <option value="75">75 rows</option>
                    <option value="100">100 rows</option>
                </select>
            </div>
        </div>

        <card-spinner
                style="height: 240px;"
                v-if="loadingInvites"
        />

        <div v-if="!loadingInvites">
            <recent-invite-list-item
                    v-for="(invite, index) of invites.data"
                    :key="invite.id"
                    :invites="invite"
                    :selectedInvites="selectedInvites"
                    :tags="tags"
                    @selected="selectInvite"
                    @updated="updateInvite"
            />
        </div>

        <pagination class="mt-3"
                    :meta="invites.meta"
                    :links="invites.links"
                    @page="fetchInvites"
        />

        <send-bulk-invites-modal
                ref="openBulkInvitesModal"
                :contacts="selectedInvites"
                @unselectAll="unselectAll"
                :isContact="isContact"
        />

        <delete-scheduled-invites-modal
                ref="openDeleteScheduledInvitesModal"
                :invites="selectedScheduled"
        />
    </div>
</template>

<script>
    import {mapState}   from 'vuex';
    import LocationInfo from './InviteLocationInfo';

    export default {
        components: {LocationInfo},

        data: () => ({
            loading           : true,
            checked           : false,
            invites           : {
                data : [],
                meta : null,
                links: null
            },
            tags              : [],
            filterBy          : '',
            sortBy            : 'send_at|desc',
            selectedInvites   : [],
            isContact         : false,
            loadingInvites    : true,
            paginationQuantity: 25,

            enableDeleteInvite: false,
            selectedScheduled : []
        }),

        created() {
            this.fetchInvites().then(() => this.loadingInvites = false);
            this.fetchTags().then(tags => this.tags = tags);
        },

        computed: {
            ...mapState({
                filter: state => state.filter,
            }),

            allInvitesSelected() {
                return _.difference(this.invites.data.map(c => c.id), this.selectedInvites).length === 0;
            },

            isLocationUser() {
                return this.spark.state.user.is_location_user;
            },

            enableSendInvite() {
                return this.selectedInvites.length;
            },

            hideForLocationUser() {
                return this.isLocationUser
                       ? `justify-content: flex-end`
                       : 'justify-content: space-between;';
            }
        },

        watch: {
            filter() {
                this.fetchInvites();
            },

            selectedScheduled() {
                if (this.selectedScheduled.length) {
                    this.enableDeleteInvite = true;
                    this.selectedScheduled.filter((invite) => {
                        if (invite.status !== 'scheduled') {
                            this.enableDeleteInvite = false;
                        }
                    });

                    return;
                }

                this.enableDeleteInvite = false;
            }
        },

        methods: {
            fetchInvites(page = 1) {
                this.loading        = true;
                this.loadingInvites = true
                let params          = {
                    ...this.filter,
                    ...{status: this.filterBy},
                    page    : page,
                    sortBy  : this.sortBy,
                    quantity: this.paginationQuantity
                };
                return this.$http.get(this.$laroute.route('api.invites.index'), {params})
                           .then(({data}) => {
                               this.invites        = data;
                               this.loading        = false;
                               this.loadingInvites = false;
                           });
            },

            updateInvite(invite) {
                this.$http.put(this.$laroute.route('api.invites.update', {invite: invite.id}), invite)
                    .catch(({response}) => this.$alert({
                        message: response.data.message || 'Something went wrong...',
                        type   : 'error',
                        title  : 'Oops!'
                    }));
            },

            fetchTags() {
                return this.$http.get(this.$laroute.route('api.tags.index')).then(({data}) => data.data);
            },

            openBulkInvitesModal() {
                this.$refs.openBulkInvitesModal.open();
            },

            openDeleteScheduledInvitesModal() {
                this.$refs.openDeleteScheduledInvitesModal.open();
            },

            unselectAll() {
                this.selectedContacts  = [];
                this.selectedScheduled = [];
            },

            handleAllInvites() {
                if (this.allInvitesSelected) {
                    this.selectedInvites   = this.selectedInvites.filter(sc => !this.invites.data.map(i => i.id).includes(sc));
                    this.selectedScheduled = this.selectedScheduled.filter(sc => !this.invites.data.map(i => i).includes(sc));
                    return;
                }

                this.invites.data.map(invite => {
                    if (!this.selectedInvites.includes(invite.id)) {
                        this.selectedInvites.push(invite.id);
                    }

                    if (!this.selectedScheduled.includes(invite)) {
                        this.selectedScheduled.push(invite);
                    }
                });
            },

            selectInvite(invite) {
                this.selectScheduled(invite);
                return this.selectedInvites.includes(invite.id)
                       ? this.selectedInvites.splice(this.selectedInvites.indexOf(invite.id), 1)
                       : this.selectedInvites.push(invite.id)
            },

            selectScheduled(invite) {
                return this.selectedScheduled.includes(invite)
                       ? this.selectedScheduled.splice(this.selectedScheduled.indexOf(invite), 1)
                       : this.selectedScheduled.push(invite);
            }
        },
    }
</script>


<style lang="scss" scoped>
    @media screen and (max-width: 991px) {
        #container-options {
            flex-direction: column-reverse;
        }

        #options {
            flex-direction: column !important;
        }

        .select-filter {
            margin-bottom: 1em;
        }
    }
</style>

